import { ModernSelect } from "@components/Select";
import { ClientContext, DEFAULT_CLIENT } from "@context/Client/ClientContext";
import LayoutDefault from "@layouts/LayoutDefault";
import { LinearProgress, SelectChangeEvent, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { useClientModels } from "@services/api";
import { ClientModel } from "@services/api/types";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

export type LayoutSelectorsElement = HTMLDivElement;

export type LayoutSelectorsProps = {
  disabled?: boolean;
  className?: string;
};

const LayoutSelectors = React.forwardRef<
  LayoutSelectorsElement,
  React.PropsWithChildren<LayoutSelectorsProps>
>(({ children, disabled = false }, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const contentHeight = `calc(${theme.customLayoutHeight.rootHeight} - ${theme.customLayoutHeight.appNavBarHeight} - ${theme.customLayoutHeight.appNavBarHeight})`;
  const { currentClient, saveCurrentClient } = useContext(ClientContext);

  const { data: clientModels, isFetching: isLoadingClientModels } =
    useClientModels(currentClient.client.id.toString());

  const handleClientModelChange = (event: SelectChangeEvent) => {
    const modelId = event.target.value as string;
    saveCurrentClient({
      client: { ...currentClient.client },
      model: clientModels?.find((model) => model.id.toString() === modelId) || DEFAULT_CLIENT.model as ClientModel
    });

    localStorage.setItem(
      "client",
      JSON.stringify({
        client: currentClient.client,
        model: currentClient.model
      })
    );
  };

  return (
    <LayoutDefault title="h">
      <Box className="w-full">
        <Box
          sx={{
            backgroundColor: "rgba(245, 247, 250, 0.5)",
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)"
          }}
          className="w-full pl-8"
        >
          <Box
            sx={{
              height: theme.customLayoutHeight.appNavBarHeight,
              width: "100%"
            }}
          >
            <Box
              sx={{
                display: "flex",
                height: "inherit",
                gap: 4,
                alignItems: "stretch",
                justifyContent: "space-between"
              }}
            >
              {isLoadingClientModels && (
                <LinearProgress color="primary" className="mt-2" />
              )}
              {!isLoadingClientModels && (
                <Box
                  sx={{
                    display: "flex",
                    minWidth: "max-content",
                    alignItems: "center",
                    gap: "1rem"
                  }}
                >
                  <Box sx={{ minWidth: 160 }}>
                    <ModernSelect
                      options={clientModels || []}
                      value={currentClient.model.id.toString()}
                      onChange={handleClientModelChange}
                      valueKey="id"
                      labelKey="name"
                      showIndicatorDot={true}
                      colorTheme="light"
                      label={t("navbar.model")}
                      labelId="model-select-modern-label"
                      id="model-select-modern"
                      disabled={disabled}
                      minWidth={160}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          id="main-content-selectors-container"
          sx={{
            height: contentHeight,
            overflow: "auto",
            width: "100%"
          }}
        >
          {children}
        </Box>
      </Box>
    </LayoutDefault>
  );
});
export default LayoutSelectors;
